import { Banner, type BannerProps } from "@made-people/blueprint-banner";
import { ButtonAsLink } from "@made-people/blueprint-button";

type StoryblokAsset = {
  src?: string;
  filename?: string;
  alt?: string;
};

type StoryblokLink = {
  href?: string;
  gearsUrl?: string;
  url?: string;
  cached_url?: string;
};

type HeadlineColor = "black" | "white";
type ButtonVariant = "white" | "green" | "black";

const buttonColorByVariant: Record<
  ButtonVariant,
  "white" | "primary" | "black"
> = {
  white: "white",
  green: "primary",
  black: "black",
};

const toHeadlineColor = (value?: string): HeadlineColor =>
  value === "black" || value === "white" ? value : "white";

const toButtonColor = (value?: string) =>
  value === "white" || value === "green" || value === "black"
    ? buttonColorByVariant[value]
    : buttonColorByVariant.white;

type PdpBannerProps = {
  headline?: string;
  headlineColore?: string;
  description?: string;
  image?: StoryblokAsset;
  buttonText?: string;
  buttonVariant?: string;
  buttonLink?: StoryblokLink;
  visibility?: BannerProps["visibility"];
};

type PdpBannerBlok = PdpBannerProps & {
  component?: string;
  productSKU?: string;
};

const productSkuList = (value: string | undefined) =>
  (value ?? "")
    .split(",")
    .map((sku) => sku.trim())
    .filter(Boolean);

const isPdpBannerBlok = (blok: unknown): blok is PdpBannerBlok =>
  typeof blok === "object" &&
  blok !== null &&
  "component" in blok &&
  blok.component === "PdpBanner";

export const findPdpBannerForProduct = (
  pageContent: unknown,
  productSku: string | undefined,
): PdpBannerBlok | undefined => {
  if (typeof productSku !== "string" || !Array.isArray(pageContent)) {
    return undefined;
  }

  return pageContent.find(
    (blok): blok is PdpBannerBlok =>
      isPdpBannerBlok(blok) &&
      productSkuList(blok.productSKU).includes(productSku),
  );
};

const linkHref = (link?: StoryblokLink) =>
  link?.gearsUrl || link?.cached_url || link?.url;

export const PdpBanner = ({
  headline,
  headlineColore,
  description,
  image,
  buttonText,
  buttonVariant,
  buttonLink,
  visibility,
}: PdpBannerProps) => {
  const src = image?.src || image?.filename;
  const href = linkHref(buttonLink);
  const hasButton = Boolean(buttonText && href);
  const headlineColor = toHeadlineColor(headlineColore);
  const buttonColor = toButtonColor(buttonVariant);

  if (!headline && !description && !src && !hasButton) {
    return null;
  }

  return (
    <div className="mt-4 lg:mt-6 [&>section>div]:h-component-spacing-6xl [&>section>div]:aspect-[auto]">
      <Banner
        isMainContent={false}
        visibility={visibility}
        variants={{
          size: "landscape",
          contentPosition: "center-center",
          colorTheme: headlineColor,
        }}
        layout={{
          spacing: { x: "none", y: "none", applySpacing: "false" },
          position: { applyPosition: "false", maxWidth: "none" },
        }}
        settings={{
          image: src
            ? {
                initial: { src, alt: image?.alt },
                displaySize: {
                  mobile: "100vw",
                  tablet: "80vw",
                  desktop: "30vw",
                },
              }
            : undefined,
          heading: headline
            ? {
                heading: headline,
                variants: { headingSize: "md", color: headlineColor },
              }
            : undefined,
          body: description,
          link: href ? { href } : undefined,
          ctas: hasButton
            ? {
                children: [
                  <ButtonAsLink
                    key="pdp-banner-cta"
                    label={buttonText}
                    link={{ href }}
                    variants={{ color: buttonColor }}
                  />,
                ],
              }
            : undefined,
        }}
      />
    </div>
  );
};
